// src/lib/fichaSheets.js
// ---------------------------------------------------------------------------
// Sesión A (S3b ampliado · 2026-09-06) · "estación en la ficha".
//
// Ajuste D17 (06-sep): `opts.cutBeforePrint` (ver cutBeforePrintFor) levanta SOLO la
// regla cortadas ≤ impresas; `≤ repetitions` rige siempre.
//
// Lógica PURA de la ficha de /cola: qué planchas del job le tocan a la estación,
// cuánto falta por plancha, cómo se resuelve un código leído por el escáner a
// una plancha DEL JOB ABIERTO (nunca de otro), y qué delta es válido. Es el
// espejo de `functions/lib/sheets.js` (validateProductionDelta): el servidor es
// el autoritativo; esto solo decide qué botones habilitar y qué rechazar antes
// de llamar a `recordProductionScan`.
// ---------------------------------------------------------------------------
import { repsOf, printedFront, printedBack, cutOf, isTwoSided, needsCut, printedDone } from './production';
import { parseScanInput } from './scanCode';
import { normalizeHriSearch } from './sheetNaming.mjs';

/** Valores válidos del campo de origen del avance (`details.source` en ai_logs). */
export const ADVANCE_SOURCES = ['cola', 'prepress', 'escaneo'];

/** Planchas del job que cuentan para la estación, ordenadas por hri. */
export function sheetsForStation(sheets = [], station) {
    const list = (sheets || []).filter((s) => {
        if (!s) return false;
        if (station === 'IMPRESION') return !!s.isPrinting;
        if (station === 'CORTE') return needsCut(s);
        return false;
    });
    return list.sort((a, b) => String(a.hri || a.id || '').localeCompare(String(b.hri || b.id || '')));
}

/** Tipo de marca que registra la estación sobre una plancha. `face` solo importa en 2 caras. */
export function typeForStation(station, sheet, face = 'front') {
    if (station === 'CORTE') return 'cut';
    if (station === 'IMPRESION') return isTwoSided(sheet) && face === 'back' ? 'printBack' : 'print';
    return null;
}

/** Contador actual de un tipo. */
export function countOf(sheet, type) {
    if (type === 'print') return printedFront(sheet);
    if (type === 'printBack') return printedBack(sheet);
    if (type === 'cut') return cutOf(sheet);
    return 0;
}

/**
 * Cuántas repeticiones se pueden sumar todavía con ese tipo. Corte en plancha
 * que imprime: hasta lo completamente impreso (misma regla que el servidor).
 */
export function faltante(sheet, type, opts) {
    const cutBeforePrint = !!opts?.cutBeforePrint;
    const reps = repsOf(sheet);
    const current = countOf(sheet, type);
    let max = reps;
    if (type === 'cut' && sheet?.isPrinting && !cutBeforePrint) max = Math.min(reps, printedDone(sheet));
    return Math.max(max - current, 0);
}

/**
 * Espejo de validateProductionDelta. Devuelve '' si el delta es válido, o el
 * motivo (texto para el operador) si no.
 */
export function deltaError(sheet, type, delta, opts) {
    const cutBeforePrint = !!opts?.cutBeforePrint;
    const d = Number(delta);
    if (!Number.isInteger(d) || d === 0) return 'El avance debe ser un entero distinto de 0.';
    if (!sheet) return 'La plancha no existe.';
    if (type === 'printBack' && !isTwoSided(sheet)) return 'Esta plancha no es de 2 caras.';
    if ((type === 'print' || type === 'printBack') && !sheet.isPrinting) return 'Esta plancha es solo corte.';
    if (type === 'cut' && !needsCut(sheet)) return 'Esta plancha no lleva corte.';
    const current = countOf(sheet, type);
    const reps = repsOf(sheet);
    if (d > 0) {
        if (current + d > reps) return `Solo faltan ${Math.max(reps - current, 0)} de ${reps}.`;
        if (type === 'cut' && sheet.isPrinting && !cutBeforePrint && current + d > printedDone(sheet)) {
            return 'No puedes cortar más de lo completamente impreso.';
        }
        return '';
    }
    if (current + d < 0) return `Solo hay ${current} marcadas.`;
    if ((type === 'print' || type === 'printBack') && needsCut(sheet) && !cutBeforePrint) {
        const field = type === 'print' ? 'printedCount' : 'printedCountBack';
        if (printedDone({ ...sheet, [field]: current + d }) < cutOf(sheet)) {
            return 'No puedes deshacer impresiones por debajo de lo ya cortado.';
        }
    }
    return '';
}

/** ¿Se puede deshacer 1 con ese tipo? */
export const canUndo = (sheet, type, opts) => deltaError(sheet, type, -1, opts) === '';

/**
 * ¿La máquina de IMPRESIÓN asignada a la ficha del job permite cortar antes de
 * imprimir? Lee `machines.allowsCutBeforePrint` (hoy solo Single Pass Postmark;
 * se setea por script). Espejo de cutBeforePrintForJob en functions/index.js.
 */
export function cutBeforePrintFor(jobId, fichas = [], machines = []) {
    const ficha = (fichas || []).find((f) => f?.id === `${jobId}__IMPRESION`);
    if (!ficha?.machineId) return false;
    return (machines || []).find((m) => m?.id === ficha.machineId)?.allowsCutBeforePrint === true;
}

/** Nombre del dorso a partir del hri frontal (…A → …B). '' si no es de 2 caras. */
export function backNameOf(sheet) {
    const hri = String(sheet?.hri || '');
    if (!isTwoSided(sheet) || !/A$/i.test(hri)) return '';
    return hri.slice(0, -1) + 'B';
}

const sameJob = (a, b) => String(a || '').replace(/^J-/, '') === String(b || '').replace(/^J-/, '');

/**
 * Resuelve lo que leyó el escáner (QR v1, QR legado o hri tecleado) a UNA plancha
 * del job abierto y al tipo de marca de la estación.
 *
 *   { ok: true,  sheet, type, matchedBy: 'id'|'hri'|'back' }
 *   { ok: false, reason }        ← nunca se escribe nada
 *
 * Reglas: un código de otro job se rechaza aunque exista; un hri se compara
 * normalizado (minúsculas, sin #) contra `hri`, `hriSearch` y el nombre del
 * dorso; en IMPRESIÓN el dorso (…B) marca `printBack`, el frente marca según
 * `face` si la plancha es de 2 caras y el código no dice la cara (QR).
 */
export function resolveScanInJob({ raw, jobId, sheets = [], station, face = 'front' }) {
    const parsed = parseScanInput(raw);
    if (!parsed) return { ok: false, reason: 'Código vacío.' };
    const candidates = sheetsForStation(sheets, station);

    if (parsed.kind === 'sheet') {
        if (!sameJob(parsed.jobId, jobId)) {
            return { ok: false, reason: `Ese código es del trabajo ${parsed.jobId}, no de ${jobId}.` };
        }
        const sheet = candidates.find((s) => s.id === parsed.id);
        if (!sheet) {
            const enJob = (sheets || []).find((s) => s.id === parsed.id);
            return { ok: false, reason: enJob ? 'Esa plancha no pasa por esta estación.' : 'Esa plancha no está en este trabajo.' };
        }
        return { ok: true, sheet, type: typeForStation(station, sheet, face), matchedBy: 'id' };
    }
    if (parsed.kind === 'piece') return { ok: false, reason: 'Es un código de pieza, no de plancha.' };
    if (parsed.kind === 'artifact') return { ok: false, reason: 'Es una etiqueta de archivo, no de plancha.' };
    if (parsed.kind === 'unknown') return { ok: false, reason: 'Código no reconocido.' };

    const term = parsed.hri;
    if (!term) return { ok: false, reason: 'Código vacío.' };
    const byFront = candidates.find((s) => normalizeHriSearch(s.hri) === term || normalizeHriSearch(s.hriSearch) === term);
    if (byFront) {
        return { ok: true, sheet: byFront, type: typeForStation(station, byFront, face), matchedBy: 'hri' };
    }
    const byBack = candidates.find((s) => backNameOf(s) && normalizeHriSearch(backNameOf(s)) === term);
    if (byBack) {
        const type = station === 'IMPRESION' ? 'printBack' : typeForStation(station, byBack, face);
        return { ok: true, sheet: byBack, type, matchedBy: 'back' };
    }
    const enJob = (sheets || []).some((s) => normalizeHriSearch(s.hri) === term);
    return { ok: false, reason: enJob ? 'Esa plancha no pasa por esta estación.' : `"${parsed.raw}" no es una plancha de ${jobId}.` };
}
